import DownloadIcon from '@mui/icons-material/Download';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Button, CircularProgress, Menu, MenuItem, Stack } from '@mui/material';
import { pdf } from '@react-pdf/renderer';
import { PDFDocument, rgb } from 'pdf-lib';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { getNameScenario } from '../../design/MultiScenario';
import StaticPDF from '../../export/MacQuayDesign_LongReport_rev_0.pdf';
import Pdf from '../../export/Pdf';
import Span from '../../shared/component/Span';
import { selectEngine } from '../../store/engine/engineSlice';
import { EReduxState } from '../../store/enum';
import { useAppSelector } from '../../store/hook';
import { selectMap } from '../../store/map/mapSlice';
import { selectProject } from '../../store/project/projectSlice';
import { selectResult } from '../../store/result/resultSlice';

interface IExportProps {}

const saveImage = (filename: string, dataUrl: string) => {
    let a = document.createElement('a');
    a.download = filename + '.png';
    a.href = dataUrl;
    a.click();
};

const savePDF = (filename: string, blob: Blob) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();

    URL.revokeObjectURL(url);
};

const Export = (props: IExportProps) => {
    const { t } = useTranslation();

    const project = useAppSelector(selectProject);
    const result = useAppSelector(selectResult);
    const engine = useAppSelector(selectEngine);
    const map = useAppSelector(selectMap);

    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const [loadingShort, setLoadingShort] = React.useState<boolean>();
    const [loadingLong, setLoadingLong] = React.useState<boolean>();

    const open = Boolean(anchorEl);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleDownloadImage = () => {
        project.data!.scenarios!.map((x, i) => {
            let nameScenario = getNameScenario(x, project.data!.engines![i]);
            saveImage(nameScenario, engine.data!.images[i]);
        });
        handleClose();
    };

    const handleDownloadLongReport = async () => {
        setLoadingLong(true);
        const blob = await pdf(<Pdf />).toBlob();
        const dynamicPdfBytes = await blob.arrayBuffer();
        const dynamicPdfDoc = await PDFDocument.load(dynamicPdfBytes);

        const response = await fetch(StaticPDF);
        const staticPdfBlob = await response.blob();
        const staticPdfBytes = await staticPdfBlob.arrayBuffer();
        const staticPdfDoc = await PDFDocument.load(staticPdfBytes);
        const pages = staticPdfDoc.getPages();
        const { width, height } = pages[0].getSize();
        const font = await staticPdfDoc.embedFont('Helvetica-Bold');
        const fontSize = 20;
        const textWidth = font.widthOfTextAtSize(project.data!.project!.title!, fontSize);
        const x = (width - textWidth) / 2 - 10;
        const y = height - 270;
        pages[0].drawText(project.data!.project!.title!, {
            x,
            y,
            size: fontSize,
            font,
            color: rgb(0.498, 0.498, 0.498),
        });

        const mergedPdfDoc = await PDFDocument.create();

        const staticPages = await mergedPdfDoc.copyPages(staticPdfDoc, staticPdfDoc.getPageIndices());
        staticPages.forEach(page => mergedPdfDoc.addPage(page));

        const dynamicPages = await mergedPdfDoc.copyPages(dynamicPdfDoc, dynamicPdfDoc.getPageIndices());
        dynamicPages.forEach(page => mergedPdfDoc.addPage(page));

        const mergedPdfBytes = await mergedPdfDoc.save();
        const pdfBlob = new Blob([mergedPdfBytes], { type: 'application/pdf' });

        savePDF(`${project.data!.project!.title!}-long-report`, pdfBlob);
        setLoadingLong(false);
        handleClose();
    };

    const handleDownloadShortReport = async () => {
        setLoadingShort(true);
        const pdfBlob = await pdf(<Pdf />).toBlob();
        savePDF(`${project.data!.project!.title!}-short-report`, pdfBlob);
        setLoadingShort(false);
        handleClose();
    };

    return (
        <Stack>
            <Button
                variant='greenLight'
                startIcon={<DownloadIcon />}
                disabled={!(result.status == EReduxState.fulfilled && engine.status == EReduxState.fulfilled && map.status === EReduxState.fulfilled)}
                id='export-button'
                endIcon={<KeyboardArrowDownIcon />}
                aria-controls={open ? 'export-menu' : undefined}
                aria-haspopup='true'
                aria-expanded={open ? 'true' : undefined}
                onClick={handleClick}>
                <Span text={t('export')} />
            </Button>
            <Menu
                id='export-menu'
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                MenuListProps={{
                    'aria-labelledby': 'export-button',
                }}>
                <MenuItem onClick={handleDownloadShortReport}>
                    <Stack direction={'row'} gap={1}>
                        <span>{t('exportShortPdf')}</span>
                        {loadingShort && <CircularProgress sx={{ margin: '0px!important' }} size={20} />}
                    </Stack>
                </MenuItem>
                <MenuItem onClick={handleDownloadLongReport}>
                    <Stack direction={'row'} gap={1}>
                        <span>{t('exportLongPdf')}</span>
                        {loadingLong && <CircularProgress sx={{ margin: '0px!important' }} size={20} />}
                    </Stack>
                </MenuItem>
                <MenuItem onClick={handleDownloadImage}>{t('exportPng')}</MenuItem>
            </Menu>
        </Stack>
    );
};

export default Export;
